import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Download, Clock, CreditCard, FileText, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/api';

const BillingHistoryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        // Try to get the billing history data from the API
        try {
          const response = await api.get('/User/billing-history');
          setBillingHistory(response.data);
        } catch (error) {
          // Fallback to mock data if API fails
          console.error('Failed to fetch billing history:', error);
          // Mock data for development purposes
          setBillingHistory([
            {
              id: 'in_1NxYz2ABC123456789',
              status: 'paid',
              date: '2023-11-15T14:32:21Z',
              amount: 49.00,
              currency: 'USD',
              description: 'Professional Plan - Monthly',
              paymentMethod: {
                brand: 'visa',
                last4: '4242',
                expiryMonth: 12,
                expiryYear: 2025
              },
              period: {
                start: '2023-11-15T00:00:00Z',
                end: '2023-12-15T00:00:00Z'
              },
              receiptUrl: '#',
              items: [
                {
                  description: 'Professional Plan Subscription',
                  amount: 49.00,
                  quantity: 1
                }
              ]
            },
            {
              id: 'in_1Mxy23DEF456789123',
              status: 'paid',
              date: '2023-10-15T14:32:21Z',
              amount: 49.00,
              currency: 'USD',
              description: 'Professional Plan - Monthly',
              paymentMethod: {
                brand: 'visa',
                last4: '4242',
                expiryMonth: 12,
                expiryYear: 2025
              },
              period: {
                start: '2023-10-15T00:00:00Z',
                end: '2023-11-15T00:00:00Z'
              },
              receiptUrl: '#',
              items: [
                {
                  description: 'Professional Plan Subscription',
                  amount: 49.00,
                  quantity: 1
                }
              ]
            },
            {
              id: 'in_1Lwx12GHI789123456',
              status: 'paid',
              date: '2023-09-15T14:32:21Z',
              amount: 49.00,
              currency: 'USD',
              description: 'Professional Plan - Monthly',
              paymentMethod: {
                brand: 'visa',
                last4: '4242',
                expiryMonth: 12,
                expiryYear: 2025
              },
              period: {
                start: '2023-09-15T00:00:00Z',
                end: '2023-10-15T00:00:00Z'
              },
              receiptUrl: '#',
              items: [
                {
                  description: 'Professional Plan Subscription',
                  amount: 49.00,
                  quantity: 1
                }
              ]
            }
          ]);
        }
      } catch (err) {
        setError('Failed to load billing history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingHistory();
  }, [navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const toggleInvoiceDetails = (invoiceId) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoiceId);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      // In a real implementation, this would call the API to get a PDF or generate one
      alert(`In a real implementation, this would download the invoice PDF for ${invoiceId}.
      
The Stripe API call would be:
const response = await api.get(\`/payments/invoices/${invoiceId}/pdf\`, { responseType: 'blob' });
const url = window.URL.createObjectURL(new Blob([response.data]));
const link = document.createElement('a');
link.href = url;
link.setAttribute('download', \`invoice-${invoiceId}.pdf\`);
document.body.appendChild(link);
link.click();
link.remove();`);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Failed to download invoice. Please try again later.');
    }
  };

  const viewReceipt = (receiptUrl) => {
    // In a real implementation, this would open the Stripe receipt URL
    window.open(receiptUrl, '_blank');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 color-transition">
      <Navbar />
      
      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-10 mt-16">
        {/* Account navigation breadcrumbs */}
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to="/account/profile" className="hover:text-blue-600 dark:hover:text-blue-400">Account</Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-blue-600 dark:text-blue-400 font-medium">Billing History</span>
        </div>

        {/* Account navigation tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
          <Link 
            to="/account/profile" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Profile
          </Link>
          <Link 
            to="/account/subscription" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Subscription
          </Link>
          <Link 
            to="/account/settings" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Settings
          </Link>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <h1 className="text-lg font-medium text-gray-900 dark:text-white">Billing History</h1>
              <div className="flex items-center">
                <Link 
                  to="/account/settings" 
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mr-3"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Payment Methods
                </Link>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                  <p>{error}</p>
                </div>
              </div>
            ) : billingHistory.length === 0 ? (
              <div className="p-6 text-center">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900/20 p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No billing history</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  You don't have any billing history yet. This is where your invoices and payments will appear once you subscribe to a paid plan.
                </p>
                <Link 
                  to="/account/subscription" 
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  View Plans
                </Link>
              </div>
            ) : (
              <div className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-200 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-100 dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {billingHistory.map((invoice) => (
                        <React.Fragment key={invoice.id}>
                          <tr 
                            className="hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-150"
                            onClick={() => toggleInvoiceDetails(invoice.id)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                                {formatDate(invoice.date)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {invoice.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatCurrency(invoice.amount, invoice.currency)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                invoice.status === 'paid' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                                  : invoice.status === 'pending' 
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                              }`}>
                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 ml-3 transition"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleInvoiceDetails(invoice.id);
                                }}
                              >
                                {expandedInvoice === invoice.id ? (
                                  <ChevronUp className="h-5 w-5" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                              </button>
                            </td>
                          </tr>
                          
                          {/* Expanded invoice details */}
                          {expandedInvoice === invoice.id && (
                            <tr className="bg-gray-100 dark:bg-gray-700">
                              <td colSpan="5" className="px-6 py-4">
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice ID</dt>
                                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{invoice.id}</dd>
                                    </div>
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Method</dt>
                                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                        {invoice.paymentMethod.brand.toUpperCase()} ending in {invoice.paymentMethod.last4}
                                      </dd>
                                    </div>
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Period</dt>
                                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                        {formatDate(invoice.period.start)} - {formatDate(invoice.period.end)}
                                      </dd>
                                    </div>
                                    <div className="col-span-2">
                                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Items</dt>
                                      <dd className="mt-1">
                                        <ul className="border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
                                          {invoice.items.map((item, index) => (
                                            <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                              <div className="w-0 flex-1 flex items-center">
                                                <span className="flex-1 w-0 truncate text-gray-900 dark:text-white">
                                                  {item.description} {item.quantity > 1 ? `(${item.quantity}x)` : ''}
                                                </span>
                                              </div>
                                              <div className="ml-4 flex-shrink-0 text-gray-900 dark:text-white">
                                                {formatCurrency(item.amount, invoice.currency)}
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      </dd>
                                    </div>
                                  </dl>
                                  <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadInvoice(invoice.id);
                                      }}
                                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                    >
                                      <Download className="h-4 w-4 mr-2" />
                                      Download Invoice
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        viewReceipt(invoice.receiptUrl);
                                      }}
                                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                    >
                                      <ExternalLink className="h-4 w-4 mr-2" />
                                      View Receipt
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          {/* Stripe integration info */}
          <div className="mt-8 bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">About Stripe Integration</h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This page demonstrates how a Stripe billing integration would work. In a real application:
              </p>
              
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6 pl-2">
                <li>Stripe would store and manage all credit cards and payment methods</li>
                <li>Invoice history would be retrieved from Stripe's API</li>
                <li>Users could download invoice PDFs generated by Stripe</li>
                <li>Receipt URLs would open Stripe-hosted receipt pages</li>
                <li>Payment status and subscription info would be synced with Stripe</li>
              </ol>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 p-4 rounded-md">
                <h3 className="text-sm font-medium mb-2 flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Main Stripe Features Used
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Stripe Customers API - Associate users with payment methods</li>
                  <li>Stripe Subscription API - Manage recurring billing</li>
                  <li>Stripe Invoices API - Retrieve and generate invoice history</li>
                  <li>Stripe Payment Methods API - Securely store payment information</li>
                  <li>Stripe Webhooks - Sync subscription status with your application</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BillingHistoryPage;